import React, { useState, useEffect } from 'react';
import { useCountries, useStates, useLgas } from '../../hooks/useGeoUnits';

export default function ProjectModal({
    isOpen,
    onClose,
    onSave,
    editData,
    title,
    organizations = [],
    selectedOrgId = '',
    isLoading = false,
    isViewMode = false
}) {
    const [formData, setFormData] = useState({
        org: '',
        name: '',
        code: '',
        description: '',
        donor: '',
        country_id: '',
        state_id: '',
        lga_id: '',
        address: '',
        start_date: '',
        end_date: '',
        budget: '',
        status: 'active',
        location_name: ''
    });

    const { data: countriesData, isLoading: loadingCountries } = useCountries();
    const { data: statesData, isLoading: loadingStates } = useStates(formData.country_id);
    const { data: lgasData, isLoading: loadingLgas } = useLgas(formData.state_id);

    const countries = countriesData?.results || countriesData || [];
    const states = statesData?.results || statesData || [];
    const lgas = lgasData?.results || lgasData || [];

    useEffect(() => {
        const formatDate = (date) => (date ? date.split('T')[0] : '');

        if (editData && isOpen) {
            setFormData({
                org: editData.org_id || editData.org?.id || editData.org || '',
                name: editData.name || '',
                code: editData.code || '',
                description: editData.description || '',
                donor: editData.donor || '',
                country_id: editData.country_id || editData.country?.id || editData.location?.country?.id || '',
                state_id: editData.state_id || editData.state?.id || editData.location?.state?.id || '',
                lga_id: editData.lga_id || editData.lga?.id || editData.location?.lga?.id || '',
                address: editData.address || editData.location?.address || '',
                start_date: formatDate(editData.start_date),
                end_date: formatDate(editData.end_date),
                budget: editData.budget || '',
                status: editData.status || 'active',
                location_name: editData.location?.name || ''
            });
        } else if (isOpen) {
            setFormData({
                org: selectedOrgId || '', name: '', code: '', description: '', donor: '',
                country_id: '', state_id: '', lga_id: '', address: '',
                start_date: '', end_date: '', budget: '', status: 'active',
                location_name: ''
            });
        }
    }, [editData, isOpen, selectedOrgId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'country_id') { updated.state_id = ''; updated.lga_id = ''; }
            if (name === 'state_id') { updated.lga_id = ''; }
            return updated;
        });
    };

    // This is the crucial part that fixes the 405/Payload issue
    const handleSubmit = (e) => {
        e.preventDefault();

        if (editData) {
            // For update: send flat project data
            const payload = {
                org: formData.org,
                name: formData.name,
                code: formData.code,
                description: formData.description,
                donor: formData.donor,
                start_date: formData.start_date,
                end_date: formData.end_date,
                budget: formData.budget,
                status: formData.status
            };
            onSave(payload);
        } else {
            // For create: send nested structure for setup endpoint
            const payload = {
                project: {
                    org: formData.org,
                    name: formData.name,
                    code: formData.code,
                    description: formData.description,
                    donor: formData.donor,
                    start_date: formData.start_date,
                    end_date: formData.end_date,
                    budget: formData.budget,
                    status: formData.status
                },
                location: {
                    name: formData.location_name || formData.name,
                    country: formData.country_id,
                    state: formData.state_id,
                    lga: formData.lga_id,
                    address: formData.address
                }
            };
            onSave(payload);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed p-15 w-full max-w-[750px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]">
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                        {title}
                    </h5>
                    <form onSubmit={handleSubmit}>
                        <div className='my-10 md:px-10 px-[7px] max-h-[65vh] overflow-auto cus-scrollbar'>
                            <div className='grid grid-cols-12 gap-20'>

                                {/* Organization Selection */}
                                <div className='form-group flex flex-col md:col-span-12 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Organization <span className='text-danger'>*</span></label>
                                    <select
                                        name='org'
                                        value={formData.org}
                                        onChange={handleChange}
                                        className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        required
                                        disabled={isLoading || isViewMode}
                                    >
                                        <option value=''>Select Organization</option>
                                        {organizations.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </select>
                                </div>

                                {/* Project Identity */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Project Name *</label>
                                    <input type='text' name='name' value={formData.name} onChange={handleChange} required disabled={isLoading || isViewMode} placeholder='e.g. Health Outreach' className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                                </div>

                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Project Code *</label>
                                    <input type='text' name='code' value={formData.code} onChange={handleChange} required disabled={isLoading || isViewMode} placeholder='e.g. PRJ-001' className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                                </div>

                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Donor *</label>
                                    <input type='text' name='donor' value={formData.donor} onChange={handleChange} required disabled={isLoading || isViewMode} placeholder='e.g. World Bank' className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                                </div>

                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Budget *</label>
                                    <input type='number' name='budget' value={formData.budget} onChange={handleChange} step='0.01' required disabled={isLoading || isViewMode} placeholder='0.00' className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                                </div>

                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Description</label>
                                    <textarea name='description' value={formData.description} onChange={handleChange} rows={2} className={`bg-bg-light border border-border w-full p-2 rounded focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={isLoading || isViewMode} />
                                </div>

                                {/* Geography Section */}
                                <div className='form-group flex flex-col md:col-span-4 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Country *</label>
                                    <select name='country_id' value={formData.country_id} onChange={handleChange} required className={`bg-bg-light border border-border p-2 rounded focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={isLoading || loadingCountries || isViewMode}>
                                        <option value=''>{loadingCountries ? 'Loading...' : 'Select Country'}</option>
                                        {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>

                                <div className='form-group flex flex-col md:col-span-4 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>State *</label>
                                    <select name='state_id' value={formData.state_id} onChange={handleChange} required className={`bg-bg-light border border-border p-2 rounded focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={isLoading || !formData.country_id || loadingStates || isViewMode}>
                                        <option value=''>{loadingStates ? 'Loading...' : 'Select State'}</option>
                                        {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>

                                <div className='form-group flex flex-col md:col-span-4 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>LGA</label>
                                    <select name='lga_id' value={formData.lga_id} onChange={handleChange} className={`bg-bg-light border border-border p-2 rounded focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={isLoading || !formData.state_id || loadingLgas || isViewMode}>
                                        <option value=''>{loadingLgas ? 'Loading...' : 'Select LGA'}</option>
                                        {lgas.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                </div>

                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Address</label>
                                    <input type='text' name='address' value={formData.address} onChange={handleChange} disabled={isLoading || isViewMode} placeholder='Street address' className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
                                </div>

                                {/* Dates */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>Start Date *</label>
                                    <input type='date' name='start_date' value={formData.start_date} onChange={handleChange} required max={new Date().toISOString().split('T')[0]} className={`bg-bg-light border border-border p-2 rounded focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={isLoading || isViewMode} />
                                </div>

                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>End Date *</label>
                                    <input type='date' name='end_date' value={formData.end_date} onChange={handleChange} required className={`bg-bg-light border border-border p-2 rounded focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={isLoading || isViewMode} />
                                </div>
                            </div>
                        </div>

                        <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                            <button type='button' onClick={onClose} className='btn btn-outline-secondary' disabled={isLoading}>{isViewMode ? 'Close' : 'Cancel'}</button>
                            {!isViewMode && (
                                <button type='submit' className='btn btn-primary' disabled={isLoading}>
                                    {isLoading ? 'Saving...' : (editData ? 'Update Project' : 'Submit')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
            <div onClick={onClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
}